import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { array, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';
/* import getCountryCodes from '../../../translations/countryCodes'; */
import { groupedByCoordinates, reducedToArray } from '../SearchMap/SearchMap.helpers';
import { buildSearchResultGroupsFromSpecificListings } from '../SearchPage.utils';
import Cards from './Cards';

import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import AuthModal from '../../../components/AuthModal/AuthModal';
import Notification from '../../../components/notification-popup/notification';
import { updateProfile } from '../../ProfileSettingsPage/ProfileSettingsPage.duck';
import { FormattedMessage, useIntl } from 'react-intl';
import { getRoomTypeTranslationId } from '../../../util/roomTypeHelpers';

const stayIcon = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082613/socialbnb/assets/icons/roomtypes/onebedroom.png';



const expIcon = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082520/socialbnb/assets/icons/baloon.webp';

const heartImg = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082500/socialbnb/assets/heart_white_filled.png';

const heartImgFill = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082499/socialbnb/assets/heart_filled.webp';

// Mobile "Load more" reveals results in batches of this many cards, independent
// of the larger server page size (RESULT_PAGE_SIZE). The next server page is only
// fetched once the already-loaded buffer has been fully revealed.
const MOBILE_PAGE_SIZE = 10;

const listingGroupKey = l =>
  Array.isArray(l) ? l.map(x => x?.id?.uuid).filter(Boolean).join('-') : '';

// Same listings by id, in the same order? `getListingsById` returns a fresh
// array on every parent render, so we compare content (not identity) to avoid
// resetting the mobile accumulator on unrelated re-renders.
const sameListingIds = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i]?.id?.uuid !== b[i]?.id?.uuid) return false;
  }
  return true;
};

const getPageFromSearchString = search => {
  if (!search || typeof search !== 'string') {
    return 1;
  }
  try {
    const qs = search.startsWith('?') ? search.slice(1) : search;
    const raw = new URLSearchParams(qs).get('page');
    if (raw == null || raw === '') {
      return 1;
    }
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  } catch {
    return 1;
  }
};

const scrollWindowToTop = () => {
  if (typeof window === 'undefined' || !window.document) {
    return;
  }
  window.scrollTo(0, 0);
  const docEl = document.documentElement;
  if (docEl) {
    docEl.scrollTop = 0;
    docEl.scrollLeft = 0;
  }
  document.body.scrollTop = 0;
  document.body.scrollLeft = 0;
  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = 0;
  }
};

const stayRoomtypeMessageId = raw =>
  getRoomTypeTranslationId(raw) || 'roomtypes.entire_accomodation';

const SearchResultCardGroup = ({
  l,
  setActiveListing,
  cardRenderSizes,
  isAuthenticated,
  favoriteFunction,
  isFavoriteListing,
  setAuthModalOpen,
  setFavAfterAuth,
  sort,
}) => {
  const [badgedListing, setBadgedListing] = useState(() => l[0]);
  const groupKey = listingGroupKey(l);

  useEffect(() => {
    setBadgedListing(l[0]);
  }, [groupKey]);

  const onSlideListingChange = useCallback(listing => {
    if (listing) {
      setBadgedListing(listing);
    }
  }, []);

  const badgeListing = badgedListing || l[0];
  const isExperienceBadge =
    badgeListing?.attributes?.publicData?.listingType === 'impact-experience';

  const stayKindMessageId = stayRoomtypeMessageId(
    badgeListing?.attributes?.publicData?.roomtype
  );

  return (
    <div className={css.cardWrapper}>
      {l.length === 1 ? (
        <div
          className={css.listingCardOuterWrapper}
          data-elbaction={`click:click ${l[0]?.attributes?.title}-uuid-${l[0]?.id?.uuid}`}
        >
          <ListingCard
            className={css.listingCard}
            key={l[0].id.uuid}
            listing={l[0]}
            renderSizes={cardRenderSizes}
            setActiveListing={setActiveListing}
            isSearchPage
          />
        </div>
      ) : (
        <Cards
          array={l}
          setActiveListing={setActiveListing}
          length={l?.length}
          onSlideListingChange={onSlideListingChange}
          sort={sort}
        />
      )}
      <div className={css.favButton}>
        {/* Type + category pills are rendered by ListingCard's own overlay
            (matches the mockup). The old roomtype/type badge here duplicated
            and overlapped them, so only the favourite (heart) button remains. */}
        <button
          onClick={e => {
            e.preventDefault();
            if (isAuthenticated) {
              favoriteFunction(l[0]);
            } else {
              setFavAfterAuth(l[0]);
              setAuthModalOpen(true);
            }
          }}
          className={css.favButtonChild}
        >
          <img src={isFavoriteListing(l) ? heartImgFill : heartImg} className={css.favImg} />
        </button>
      </div>
    </div>
  );
};

const SearchResultsPanelComponent = props => {
  const [notification, setNotification] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [favorite, Setfavorite] = useState(false);
  const [favAfterAuth, setFavAfterAuth] = useState({});
  const intl = useIntl();
  const urlPageRef = useRef(null);
  const {
    className,
    rootClassName,
    listings,
    pagination,
    search,
    setActiveListing,
    isMapVariant,
    currentUser,
    isAuthenticated,
    onUpdateProfile,
    nearbyLabelIndex,
    location,
    onLoadMore,
  } = props;

  // Mobile shows a "Load more" button that accumulates results client-side
  // (Option B) instead of the desktop numbered pagination.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 767 : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const currentPage = pagination?.page ?? (Number(search?.page) || 1);
  // Signature of the search excluding `page`: when it changes we started a new
  // search and must reset the accumulator instead of appending.
  const searchWithoutPage = { ...(search || {}) };
  delete searchWithoutPage.page;
  const searchSignature = JSON.stringify(searchWithoutPage);

  const [accumulatedListings, setAccumulatedListings] = useState(listings);
  // How many cards are currently revealed on mobile. Starts at one batch and
  // grows by MOBILE_PAGE_SIZE per "Load more" click.
  const [visibleCount, setVisibleCount] = useState(MOBILE_PAGE_SIZE);
  const accSignatureRef = useRef(null);
  useEffect(() => {
    if (!isMobile) {
      return;
    }
    const isNewSearch = accSignatureRef.current !== searchSignature;
    accSignatureRef.current = searchSignature;

    // A genuinely new search (filters/location changed) → reset to the current
    // page's listings and collapse back to the first batch. This is the ONLY
    // place `visibleCount` is reset: the parent re-renders on any click (a global
    // click listener calls setState) and hands us a brand-new `listings` array
    // each time, so resetting the reveal on every effect run — as the old
    // `currentPage <= 1` branch did — silently undid "Load more" on the next
    // re-render, making the button appear dead.
    if (isNewSearch) {
      setAccumulatedListings(listings);
      setVisibleCount(MOBILE_PAGE_SIZE);
      return;
    }

    // Same search, still on the first page → keep the accumulator in sync with
    // the incoming listings (needed so results appear once the request resolves)
    // but by content, so an unrelated re-render doesn't churn state or reset the
    // number of revealed cards.
    if (currentPage <= 1) {
      setAccumulatedListings(prev => (sameListingIds(prev, listings) ? prev : listings));
      return;
    }

    // Same search, a later page ("Load more") → union the incoming listings into
    // what's already shown, de-duplicated by id, so earlier pages are kept and
    // the new ones appended (infinite-scroll style). A union rather than a
    // page-number check avoids a race where `currentPage` (from the URL) advances
    // before the new listings arrive — which previously caused the accumulator to
    // be replaced with only the new page instead of extended.
    setAccumulatedListings(prev => {
      const seen = new Set(prev.map(l => l.id.uuid));
      const additions = listings.filter(l => !seen.has(l.id.uuid));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, [listings, searchSignature, currentPage, isMobile]);

  const effectiveListings = isMobile ? accumulatedListings : listings;

  useEffect(() => {
    const page = getPageFromSearchString(location?.search);
    if (urlPageRef.current !== null && urlPageRef.current !== page) {
      // Don't jump to the top when appending more results on mobile (load-more).
      if (!(isMobile && page > urlPageRef.current)) {
        scrollWindowToTop();
      }
    }
    urlPageRef.current = page;
  }, [location?.search]);
  const classes = classNames(rootClassName || css.root, className);

  // `nearbyLabelIndex` marks, within the *latest fetched page*, where the
  // "nearby recommendations" tail begins (the API appends recs after the real
  // matches on the last page of a country/continent search). On mobile the panel
  // accumulates pages, and only that final page carries a nearby tail, so the
  // recs always sit at the very end of the accumulated array. Slicing the
  // accumulated list at the raw per-page index would wrongly drop every earlier
  // page — which is what collapsed "Load more" down to a single card. Instead
  // translate the per-page index into a count of trailing recs and trim from the
  // end. On desktop / a single mobile page `effectiveListings.length` equals the
  // current page length, so this reduces to the original `slice(0, index)`.
  const nearbyTailCount =
    nearbyLabelIndex != null ? Math.max(0, listings.length - nearbyLabelIndex) : 0;
  const nearbyBoundary =
    nearbyLabelIndex == null ? null : Math.max(0, effectiveListings.length - nearbyTailCount);

  // Real matches sit before the boundary; the "You might also like" recommendations
  // are the trailing tail after it (Ranim's feature). Computed off nearbyBoundary so
  // the recs stay correct with mobile "Load more" accumulation, not the raw index.
  const specificListings =
    nearbyBoundary !== null
      ? nearbyBoundary > 0
        ? reducedToArray(groupedByCoordinates(effectiveListings.slice(0, nearbyBoundary)))
        : null
      : reducedToArray(groupedByCoordinates(effectiveListings));

  const recommendationListings =
    nearbyBoundary !== null ? effectiveListings.slice(nearbyBoundary) : [];

  const recommendationGroups =
    recommendationListings.length > 0
      ? reducedToArray(groupedByCoordinates(recommendationListings))
      : null;
      
  /*   function sortListings(listings, code) {
    return listings.sort((a, b) => {
        const aCountry = a.attributes.publicData.country;
        const bCountry = b.attributes.publicData.country;

        if (aCountry === code && bCountry !== code) {
            return -1; // a comes first
        } else if (aCountry !== code && bCountry === code) {
            return 1;  // b comes first
        } else {
            return 0;  // no change in order
        }
    });
   } */
  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="SearchPage"
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  const favoriteFunction = async listing => {
    let listingId = listing.id;
    if (!currentUser.attributes.profile.publicData.favoritList) {
      currentUser.attributes.profile.publicData.favoritList = [];
    }
    const favoritList = currentUser.attributes.profile.publicData.favoritList;
    const index = favoritList.findIndex(item => item.listId === listingId.uuid);
    if (index !== -1) {
      favoritList.splice(index, 1);
      await onUpdateProfile({ publicData: { favoritList } });
      Setfavorite(false);
      let notif = intl.formatMessage({ id: 'favoritePage.removed' });
      setNotification(notif);
    } else {
      favoritList.push({ listId: listingId.uuid });
      await onUpdateProfile({ publicData: { favoritList } });
      Setfavorite(true);
      let notif = intl.formatMessage({ id: 'favoritePage.added' });
      setNotification(notif);
    }
  };

  const isFavoriteListing = listing => {
    if (!isAuthenticated || !currentUser) {
      return false;
    }
    const checkFavorite = list => {
      let listingId = list.id;
      if (!currentUser?.attributes?.profile?.publicData?.favoritList) {
        currentUser.attributes.profile.publicData.favoritList = [];
      }
      const favoritList = currentUser.attributes.profile.publicData.favoritList;
      const index = favoritList.findIndex(item => item.listId == listingId.uuid);
      return index !== -1;
    };

    if (Array.isArray(listing)) {
      for (let item of listing) {
        if (checkFavorite(item)) {
          return true;
        }
      }
      return false;
    } else {
      return checkFavorite(listing);
    }
  };
  const actionAfterAuth = () => {
    setTimeout(() => {
      favoriteFunction(favAfterAuth);
    }, 1000);
  };

  const newListing = buildSearchResultGroupsFromSpecificListings(specificListings);

  const selectedSort = search?.sort;
  const isPriceSort = selectedSort === 'price' || selectedSort === '-price';

  // Representative price of a group: for "highest" (price) use the group's most
  // expensive listing, for "lowest" (-price) its cheapest — the same listing the
  // card badge shows — so the grid order and the shown "from X€" stay consistent.
  // Grouping-by-coordinates scrambles the API's flat price order, so we re-sort
  // the groups here client-side instead of trusting the raw API order.
  const getGroupPrice = (group, descending) => {
    const items = Array.isArray(group) ? group : [group];
    const amounts = items
      .map(item => item?.attributes?.price?.amount)
      .filter(amount => typeof amount === 'number');
    if (!amounts.length) {
      return null;
    }
    return descending ? Math.max(...amounts) : Math.min(...amounts);
  };

  const sortedNewListing = !newListing
    ? newListing
    : isPriceSort
    ? [...newListing].sort((a, b) => {
        const descending = selectedSort === 'price';
        const aPrice = getGroupPrice(a, descending);
        const bPrice = getGroupPrice(b, descending);
        // Groups without any price go last regardless of direction.
        if (aPrice == null && bPrice == null) return 0;
        if (aPrice == null) return 1;
        if (bPrice == null) return -1;
        return descending ? bPrice - aPrice : aPrice - bPrice;
      })
    : selectedSort
    ? newListing
    : newListing.sort((a, b) => {
          const getBoostScore = group => {
        if (Array.isArray(group)) {
          return Math.max(...group.map(item => item?.attributes?.publicData?.boostScore || 0));
        }
        return group?.attributes?.publicData?.boostScore || 0;
      };

      const getHighestRating = group => {
        if (Array.isArray(group)) {
          return Math.max(
            ...group.map(item => {
              const publicData = item?.attributes?.publicData || {};
              const finalReviews = publicData.finalReviews || { count: 0, rating: 0 };
              const googleReviews = publicData.location?.reviews || publicData.googleReviews || [];

              if (finalReviews.count > 0 && finalReviews.rating > 0) {
                return finalReviews.rating;
              }

              if (googleReviews.length > 0) {
                const totalRating = googleReviews.reduce(
                  (sum, review) => sum + (review.rating || 0),
                  0
                );
                const averageRating = totalRating / googleReviews.length;
                return Math.round(averageRating * 10) / 10;
              }

              return 0;
            })
          );
        }

        const publicData = group?.attributes?.publicData || {};
        const finalReviews = publicData.finalReviews || { count: 0, rating: 0 };
        const googleReviews = publicData.location?.reviews || publicData.googleReviews || [];

        if (finalReviews.count > 0 && finalReviews.rating > 0) {
          return finalReviews.rating;
        }

        if (googleReviews.length > 0) {
          const totalRating = googleReviews.reduce((sum, review) => sum + (review.rating || 0), 0);
          const averageRating = totalRating / googleReviews.length;
          return Math.round(averageRating * 10) / 10;
        }

        return 0;
      };

      const aBoostScore = getBoostScore(a);
      const bBoostScore = getBoostScore(b);
      if (bBoostScore !== aBoostScore) {
        return bBoostScore - aBoostScore;
      }

      const aHighestRating = getHighestRating(a);
      const bHighestRating = getHighestRating(b);

      return bHighestRating - aHighestRating;
    });

  // Mobile only: reveal the accumulated results one MOBILE_PAGE_SIZE batch at a
  // time. Desktop renders the full page and uses numbered pagination instead.
  const totalCards = sortedNewListing ? sortedNewListing.length : 0;
  const renderedListing =
    isMobile && sortedNewListing ? sortedNewListing.slice(0, visibleCount) : sortedNewListing;

  // "Load more" stays visible while there are either buffered cards still hidden
  // or further server pages to fetch; it disappears once everything is shown.
  const hasMorePages = !!(pagination && pagination.page < pagination.totalPages);
  const hasBufferedMore = visibleCount < totalCards;
  const showMobileLoadMore = isMobile && onLoadMore && (hasBufferedMore || hasMorePages);

  const handleLoadMoreClick = () => {
    const nextVisible = visibleCount + MOBILE_PAGE_SIZE;
    setVisibleCount(nextVisible);
    // Only hit the server once the current buffer is exhausted; otherwise we just
    // reveal more of what's already loaded.
    if (nextVisible >= totalCards && hasMorePages) {
      onLoadMore(pagination.page + 1);
    }
  };

  const loadMoreControl = showMobileLoadMore ? (
    <div className={css.loadMoreWrapper}>
      <button
        type="button"
        className={css.loadMoreButton}
        onClick={handleLoadMoreClick}
        data-elb="searchp"
        data-elbaction={`click: click loadMore-${visibleCount}`}
      >
        <FormattedMessage id="SearchResultsPanel.loadMore" defaultMessage="Load more" />
      </button>
    </div>
  ) : null;
  const sortedRecommendationGroups = recommendationGroups
    ? [...recommendationGroups].sort((a, b) => {
        const getBoostScore = group => {
          const items = Array.isArray(group) ? group : [group];
          return Math.max(...items.map(item => item?.attributes?.publicData?.boostScore || 0));
        };

        return getBoostScore(b) - getBoostScore(a);
      })
    : null;

  return (
    <div className={classes}>
      {/* Render AuthModal in a portal at document body level to ensure proper z-index */}
      {typeof window !== 'undefined' &&
        authModalOpen &&
        ReactDOM.createPortal(
          <AuthModal
            isOpen={authModalOpen}
            setOpen={setAuthModalOpen}
            redirectToCheckout={actionAfterAuth}
          />,
          document.body
        )}
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {notification && (
          <Notification message={notification} onClose={() => setNotification(null)} />
        )}
        {renderedListing &&
          renderedListing.map((l, index) => {
            const pageKey = pagination?.page ?? search?.page ?? 1;
            // On mobile the list accumulates across pages, so keep keys stable (no
            // page suffix) to avoid re-mounting earlier cards on each "Load more".
            const cardKey = isMobile
              ? listingGroupKey(l) || `i${index}`
              : `${listingGroupKey(l) || `i${index}`}-p${pageKey}`;
            return (
            <SearchResultCardGroup
              key={cardKey}
              l={l}
              setActiveListing={setActiveListing}
              cardRenderSizes={cardRenderSizes}
              isAuthenticated={isAuthenticated}
              favoriteFunction={favoriteFunction}
              isFavoriteListing={isFavoriteListing}
              setAuthModalOpen={setAuthModalOpen}
              setFavAfterAuth={setFavAfterAuth}
              sort={selectedSort}
            />
            );
          })}

          {sortedRecommendationGroups && sortedRecommendationGroups.length > 0 ? (
            <>
              <div className={css.recommendationLabelRow}>
                <FormattedMessage
                  id="SearchResultsPanel.youMightAlsoLike"
                  defaultMessage="You might also like"
                />
              </div>

              {sortedRecommendationGroups.map((l, index) => {
                const cardKey = `${listingGroupKey(l) || `rec${index}`}-recommendation`;
                return (
                  <SearchResultCardGroup
                    key={cardKey}
                    l={l}
                    setActiveListing={setActiveListing}
                    cardRenderSizes={cardRenderSizes}
                    isAuthenticated={isAuthenticated}
                    favoriteFunction={favoriteFunction}
                    isFavoriteListing={isFavoriteListing}
                    setAuthModalOpen={setAuthModalOpen}
                    setFavAfterAuth={setFavAfterAuth}
                    sort={selectedSort}
                  />
                );
              })}
            </>
          ) : null}

        {props.children}

        {isMobile ? loadMoreControl : null}
      </div>
      {isMobile ? null : paginationLinks}
    </div>
  );
};

SearchResultsPanelComponent.defaultProps = {
  children: null,
  className: null,
  listings: [],
  pagination: null,
  rootClassName: null,
  search: null,
  isMapVariant: true,
};

SearchResultsPanelComponent.propTypes = {
  children: node,
  className: string,
  listings: array,
  pagination: propTypes.pagination,
  rootClassName: string,
  search: object,
  isMapVariant: bool,
  onLoadMore: func,
  location: shape({
    search: string.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { isAuthenticated } = state.auth;
  return { currentUser, isAuthenticated };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});
const SearchResultsPanel = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(SearchResultsPanelComponent);

export default SearchResultsPanel;
